/**
 * External Guidance Credits & Attribution Reference
 * Maps external meditation & mindfulness practices to their respective teachers,
 * organizations, and canonical courtesy lines.
 */

export const EXTERNAL_GUIDANCE_CREDITS = {
  // Jack Kornfield
  mindfullovingwitnessmeditationjackkornfield: {
    creator: "Jack Kornfield",
    courtesy: "Courtesy of Jack Kornfield",
  },
  infinitemomentmusicmeditationjackkornfieldfeatsuperpositionformlessandthealbumleaf: {
    creator: "Jack Kornfield",
    courtesy: "Courtesy of Jack Kornfield",
  },
  equanimitypeace: {
    creator: "Jack Kornfield",
    courtesy: "Courtesy of Jack Kornfield",
  },
  awarenessoffeelings: {
    creator: "Jack Kornfield",
    courtesy: "Courtesy of Jack Kornfield",
  },
  mindfulnessofbreathexternal: {
    creator: "Jack Kornfield",
    courtesy: "Courtesy of Jack Kornfield",
  },
  lovingkindnessmeditation: {
    creator: "Jack Kornfield",
    courtesy: "Courtesy of Jack Kornfield",
  },

  // davidji
  minuteguidedmeditationforpositiveenergyreleasingfeardavidji: {
    creator: "davidji",
    courtesy: "Courtesy of davidji",
  },
  minuteguidedmeditationformanifestingyourdreamsdesiresdavidji: {
    creator: "davidji",
    courtesy: "Courtesy of davidji",
  },
  slowingdownguidedmeditation: {
    creator: "davidji",
    courtesy: "Courtesy of davidji",
  },
  hammockbeditationinnature: {
    creator: "davidji",
    courtesy: "Courtesy of davidji",
  },

  // Tara Brach
  meditationmindfulbodyscan: {
    creator: "Tara Brach",
    courtesy: "Courtesy of Tara Brach",
  },
  magicbreaths: {
    creator: "Tara Brach",
    courtesy: "Courtesy of Tara Brach",
  },
  nourishinghappinesswithrain: {
    creator: "Tara Brach",
    courtesy: "Courtesy of Tara Brach",
  },
  pathwaytoinnerpeace: {
    creator: "Tara Brach",
    courtesy: "Courtesy of Tara Brach",
  },
  mindfulnessofthoughts: {
    creator: "Tara Brach",
    courtesy: "Courtesy of Tara Brach",
  },
  thearisingofthoughts: {
    creator: "Tara Brach",
    courtesy: "Courtesy of Tara Brach",
  },
  recognizingsensations: {
    creator: "Tara Brach",
    courtesy: "Courtesy of Tara Brach",
  },
  connectingwithourbodies: {
    creator: "Tara Brach",
    courtesy: "Courtesy of Tara Brach",
  },
  guidedmeditationonpainanddiscomfort: {
    creator: "Tara Brach",
    courtesy: "Courtesy of Tara Brach",
  },
  expandingourcapacityforselfcompassion: {
    creator: "Tara Brach",
    courtesy: "Courtesy of Tara Brach",
  },

  // Jonathan Foust
  standingmeditation: {
    creator: "Jonathan Foust",
    courtesy: "Courtesy of Jonathan Foust",
  },
  walkingmeditation: {
    creator: "Jonathan Foust",
    courtesy: "Courtesy of Jonathan Foust",
  },
  seatedmovementsequence: {
    creator: "Jonathan Foust",
    courtesy: "Courtesy of Jonathan Foust",
  },
  chestopeningseatedmovementsequence: {
    creator: "Jonathan Foust",
    courtesy: "Courtesy of Jonathan Foust",
  },

  // Aliza (Mindsherpa Check-ins & PEARLI)
  morningfocus: {
    creator: "Aliza",
    courtesy: "Guided by Aliza",
  },
  middayreset: {
    creator: "Aliza",
    courtesy: "Guided by Aliza",
  },
  nightreflection: {
    creator: "Aliza",
    courtesy: "Guided by Aliza",
  },
  pearliwellbeing: {
    creator: "Aliza",
    courtesy: "Guided by Aliza",
  },
};

/**
 * Normalizes a key for fuzzy lookup
 */
const normalizeKey = (str) => (str || "").toLowerCase().replace(/[^a-z0-9]/g, "");

/**
 * Returns { creator, courtesy } attribution for any exercise object or identifier.
 * Priority:
 * 1. Explicit properties on the exercise (courtesy / creator)
 * 2. Lookup by exercise defaulturl / id
 * 3. Fallback heuristic by title keywords (e.g. "Jack Kornfield", "davidji", "Tara Brach", "Jonathan Foust")
 */
export const getGuidanceAttribution = (exercise) => {
  if (!exercise) return null;

  // If already directly present on the exercise object
  if (exercise.courtesy && exercise.creator) {
    return {
      creator: exercise.creator,
      courtesy: exercise.courtesy,
    };
  }

  if (exercise.courtesy) {
    const creator = exercise.courtesy.replace(/^courtesy\s+of\s+/i, "").trim();
    return { creator, courtesy: exercise.courtesy };
  }

  if (exercise.creator) {
    return {
      creator: exercise.creator,
      courtesy: `Courtesy of ${exercise.creator}`,
    };
  }

  // Lookup by URL / ID
  const urlKey = normalizeKey(exercise.defaulturl || exercise.id || exercise.exerciseURL);
  if (urlKey && EXTERNAL_GUIDANCE_CREDITS[urlKey]) {
    return EXTERNAL_GUIDANCE_CREDITS[urlKey];
  }

  // Lookup by title matching
  const title = (exercise.title || exercise.titleText || "").toLowerCase();

  if (title.includes("jack kornfield")) {
    return {
      creator: "Jack Kornfield",
      courtesy: "Courtesy of Jack Kornfield",
    };
  }
  if (title.includes("davidji")) {
    return {
      creator: "davidji",
      courtesy: "Courtesy of davidji",
    };
  }
  if (
    title.includes("tara brach") ||
    title.includes("9 magic breaths") ||
    title.includes("nourishing happiness with rain")
  ) {
    return {
      creator: "Tara Brach",
      courtesy: "Courtesy of Tara Brach",
    };
  }
  if (
    title.includes("jonathan foust") ||
    title.includes("standing meditation") ||
    title.includes("walking meditation")
  ) {
    return {
      creator: "Jonathan Foust",
      courtesy: "Courtesy of Jonathan Foust",
    };
  }

  // Normalized title lookup across credit keys
  const normTitle = normalizeKey(title);
  if (normTitle) {
    for (const [key, val] of Object.entries(EXTERNAL_GUIDANCE_CREDITS)) {
      if (normTitle.includes(key) || key.includes(normTitle)) {
        return val;
      }
    }
  }

  return null;
};
